import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../config/api';
import {
  adminOutlineBtnSx,
  adminSectionTitleSx,
  adminSolidBtnSx,
  adminSurfaceCardSx,
} from '../../components/AdminChrome';
import { DOCUMENT } from '../../constants/documentTheme';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 0, bgcolor: DOCUMENT.ivory },
};

export default function PrivateProposalsPanel({ t, inquiryIdFilter }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [proposals, setProposals] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [packageId, setPackageId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [newInquiryId, setNewInquiryId] = useState(inquiryIdFilter ? String(inquiryIdFilter) : '');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (inquiryIdFilter) params.inquiryId = inquiryIdFilter;
      const { data } = await api.get('/admin/private/proposals', { params });
      setProposals(data.proposals || []);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [inquiryIdFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setError('');
    try {
      const { data } = await api.get(`/admin/private/proposals/${id}`);
      setDetail(data);
      setPackageId(data.proposal?.sourcePackageId ? String(data.proposal.sourcePackageId) : '');
      setPartnerId(data.proposal?.partnerId ? String(data.proposal.partnerId) : '');
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    }
  };

  const createProposal = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/admin/private/proposals', {
        inquiryId: Number(newInquiryId),
      });
      await load();
      await openDetail(data.proposal.id);
      setNotice(t('adminPrivate.proposalCreated'));
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, successKey) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      if (selectedId) await openDetail(selectedId);
      await load();
      if (successKey) setNotice(t(successKey));
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }}>{notice}</Alert> : null}

      <Card sx={{ ...adminSurfaceCardSx, p: 2, mb: 2 }} elevation={0}>
        <Typography sx={adminSectionTitleSx}>{t('adminPrivate.newProposal')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            label={t('adminPrivate.fields.inquiryId')}
            value={newInquiryId}
            onChange={(e) => setNewInquiryId(e.target.value)}
            sx={{ ...fieldSx, minWidth: 160 }}
            size="small"
          />
          <Button
            startIcon={<AddIcon />}
            disabled={busy || !newInquiryId}
            sx={adminSolidBtnSx}
            onClick={createProposal}
          >
            {t('adminPrivate.createProposal')}
          </Button>
        </Stack>
      </Card>

      {proposals.length === 0 ? (
        <Typography sx={{ color: DOCUMENT.ink3, py: 4, textAlign: 'center' }}>
          {t('adminPrivate.emptyProposals')}
        </Typography>
      ) : (
        <TableContainer sx={adminSurfaceCardSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('adminPrivate.cols.title')}</TableCell>
                <TableCell>{t('adminPrivate.cols.client')}</TableCell>
                <TableCell>{t('adminPrivate.cols.status')}</TableCell>
                <TableCell>{t('adminPrivate.cols.package')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.map((row) => (
                <TableRow key={row.id} hover selected={selectedId === row.id}>
                  <TableCell>
                    {row.title}
                    {row.isIllustrative ? (
                      <Typography sx={{ fontSize: 11, color: DOCUMENT.accent }}>
                        {t('adminPrivate.illustrativeOnly')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.clientName}</TableCell>
                  <TableCell>{t(`adminPrivate.proposalStatuses.${row.status}`, { defaultValue: row.status })}</TableCell>
                  <TableCell>{row.packageTitle || '—'}</TableCell>
                  <TableCell>
                    <Button size="small" sx={adminOutlineBtnSx} onClick={() => openDetail(row.id)}>
                      {t('adminPrivate.open')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {detail?.proposal ? (
        <Card sx={{ ...adminSurfaceCardSx, p: 2.5, mt: 3 }} elevation={0}>
          <Typography sx={adminSectionTitleSx}>{detail.proposal.title}</Typography>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>
            {t(`adminPrivate.proposalStatuses.${detail.proposal.status}`)}
            {detail.proposal.isIllustrative ? ` · ${t('adminPrivate.illustrativeOnly')}` : ''}
            {detail.proposal.existingTripId ? ` · Trip #${detail.proposal.existingTripId}` : ''}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label={t('adminPrivate.fields.packageId')}
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              sx={fieldSx}
              size="small"
            />
            <TextField
              label={t('adminPrivate.fields.partnerId')}
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              sx={fieldSx}
              size="small"
              helperText={t('adminPrivate.fields.partnerIdHint')}
            />
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Button
              disabled={busy || !packageId}
              sx={adminOutlineBtnSx}
              onClick={() => run(
                () => api.post(`/admin/private/proposals/${detail.proposal.id}/attach-package`, {
                  packageId: Number(packageId),
                }),
                'adminPrivate.packageAttached'
              )}
            >
              {t('adminPrivate.actions.attachPackage')}
            </Button>
            <Button
              disabled={busy}
              sx={adminOutlineBtnSx}
              onClick={() => run(
                () => api.post(`/admin/private/proposals/${detail.proposal.id}/quotes`, {
                  retailAmount: detail.proposal.clientPriceIfSupplierConfirmed,
                  markVerified: true,
                  markSupplierConfirmed: true,
                  pricingVisibility: 'internal',
                }),
                'adminPrivate.supplierConfirmed'
              )}
            >
              {t('adminPrivate.actions.confirmSupplierQuote')}
            </Button>
            <Button
              disabled={busy}
              sx={adminOutlineBtnSx}
              onClick={() => run(
                () => api.post(`/admin/private/proposals/${detail.proposal.id}/approve`),
                'adminPrivate.proposalApproved'
              )}
            >
              {t('adminPrivate.actions.approveInternal')}
            </Button>
            <Button
              disabled={busy}
              sx={adminOutlineBtnSx}
              onClick={() => run(
                () => api.post(`/admin/private/proposals/${detail.proposal.id}/create-concept-trip`, {
                  partnerId: partnerId ? Number(partnerId) : undefined,
                }),
                'adminPrivate.conceptTripCreated'
              )}
            >
              {t('adminPrivate.actions.createConceptTrip')}
            </Button>
            <Button
              disabled={busy}
              sx={adminSolidBtnSx}
              onClick={() => run(
                () => api.post(`/admin/private/inquiries/${detail.proposal.inquiryId}/fulfill`, {
                  proposalId: detail.proposal.id,
                  packageId: packageId ? Number(packageId) : undefined,
                }),
                'adminPrivate.fulfilled'
              )}
            >
              {t('adminPrivate.actions.fulfillItinerary')}
            </Button>
            <Button
              disabled={busy}
              sx={adminOutlineBtnSx}
              onClick={() => run(
                () => api.post(`/admin/private/proposals/${detail.proposal.id}/share`),
                'adminPrivate.shareCreated'
              )}
            >
              {t('adminPrivate.actions.shareLink')}
            </Button>
            {detail.proposal.existingTripId ? (
              <Button
                component={RouterLink}
                to={`/trips/${detail.proposal.existingTripId}`}
                sx={adminOutlineBtnSx}
              >
                {t('adminPrivate.actions.openTrip')}
              </Button>
            ) : null}
          </Stack>
        </Card>
      ) : null}
    </Box>
  );
}
